import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtUSD } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { ShieldCheck, Eye, Send } from "lucide-react";

export const WeeklyDigestCard = () => {
  const { lang } = useApp();
  const [enabled, setEnabled] = useState(true);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/notifications/preferences"); setEnabled(data.weekly_digest !== false); }
      catch (e) { console.error("Failed to load weekly digest preference:", e); }
    })();
  }, []);

  const toggle = async (v) => {
    setEnabled(v);
    try {
      await api.patch("/notifications/preferences", { weekly_digest: v });
      toast.success(v ? "Weekly halal note on" : "Weekly halal note off");
    } catch (e) { toast.error("Could not save that preference"); setEnabled(!v); }
  };

  const showPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/digest/weekly/preview", { params: { lang } });
      setPreview(data); setOpen(true);
    } catch (e) { toast.error("Could not build your weekly note"); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/weekly/send", null, { params: { lang } });
      toast.success("Weekly note sent", { description: data.subject });
      setOpen(false);
    } catch (e) { toast.error("Could not send the weekly note"); }
    finally { setBusy(false); }
  };

  const d = preview?.digest;
  const s = d?.summary;

  return (
    <Card className="p-5 shadow-premium" data-testid="weekly-digest-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          <ShieldCheck className="h-4 w-4 text-[#3E2E65]" />Weekly halal note
        </div>
        <Switch checked={enabled} onCheckedChange={toggle} data-testid="weekly-digest-toggle" />
      </div>
      <p className="text-[11px] text-muted-foreground mb-4">
        Sunday morning: what changed on Shariah status, the income that landed, what is owed in
        purification, and the cleanest compliant names in the sectors you own.
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" className="gap-1.5" onClick={showPreview} disabled={busy}
          data-testid="weekly-digest-preview-button">
          <Eye className="h-3.5 w-3.5" />Preview
        </Button>
        <Button size="sm" variant="ghost" className="gap-1.5" onClick={sendNow} disabled={busy}
          data-testid="weekly-digest-send-button">
          <Send className="h-3.5 w-3.5" />Send me one now
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="weekly-digest-preview-dialog">
          <DialogHeader>
            <DialogTitle>{preview?.subject || "Your halal week"}</DialogTitle>
            <DialogDescription>This is exactly what lands in your inbox on Sunday.</DialogDescription>
          </DialogHeader>
          {d && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[["Shariah compliant", `${s.compliant} of ${s.screened}`],
                  ["Needs review", String(s.needs_review)],
                  ["Income to purify", fmtUSD(s.purification_owed_usd)],
                  ["Dividends received", fmtUSD(d.dividends.total_usd)]].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-secondary/60 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                    <div className="numeric text-lg font-semibold">{v}</div>
                  </div>
                ))}
              </div>
              <pre className="rounded-lg border bg-secondary/40 p-3 text-[11px] leading-relaxed whitespace-pre-wrap max-h-64 overflow-auto"
                data-testid="weekly-digest-preview-body">{preview.body}</pre>
              <Button className="w-full gap-1.5" onClick={sendNow} disabled={busy} data-testid="weekly-digest-dialog-send">
                <Send className="h-4 w-4" />Send this to my inbox
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
